import React, { useEffect, useId, useRef, useState } from 'react';
import { Pencil } from 'lucide-react';
import RecordChip from './RecordChip';

// THE field for picking a linked record on a form — a Customer on a Quote
// or Sales Order, and any other "pick one record" field (ui-architecture.md
// "Record Lookup Field"). Once a record is picked it shows as a RecordChip:
// the record's icon and name in the accent blue, which opens the record. Same
// look whether the form is locked or not. On an editable form a Change
// button turns the chip back into the search box; picking a result replaces
// the record, and Escape or leaving the box keeps the one already picked.
//
// origin, noun and hasUnsavedChanges pass through to the chip (opening the
// record, the way back, and "Leave without saving?").
//
// value: the picked record as { id, label }, or null. search(term) resolves
// to matching records; optionLabel(record) is how each reads in the list.
// onSelect(record) is the form's own handling of a pick (it sets value).
// href and icon describe value's record; readOnly locks the field.

const DEBOUNCE_MS = 250;
const BOX_STYLE = {
  display: 'flex',
  alignItems: 'center',
  maxWidth: '100%',
  boxSizing: 'border-box',
  minHeight: 34,
  padding: '3px 4px',
  border: '1px solid #a9a9a9',
  borderRadius: 4,
  background: '#fff',
};

function RecordLookupField({
  value,
  search,
  optionLabel = (r) => r.name,
  onSelect,
  href,
  icon: Icon,
  readOnly = false,
  placeholder = 'Search...',
  origin,
  noun,
  hasUnsavedChanges,
  openTitle = 'Open',
  changeTitle = 'Change',
  width = 300,
}) {
  const inputRef = useRef(null);
  const listId = useId();
  const [editing, setEditing] = useState(false);
  const [term, setTerm] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const showInput = !readOnly && (editing || !value);

  useEffect(() => {
    if (!term.trim()) { setResults([]); setOpen(false); setSearching(false); return undefined; }
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(() => {
      Promise.resolve(search(term.trim()))
        .then((rows) => { if (!cancelled) { setResults(rows || []); setActive(0); setOpen(true); } })
        .catch(() => { if (!cancelled) setResults([]); })
        .finally(() => { if (!cancelled) setSearching(false); });
    }, DEBOUNCE_MS);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term]);

  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const close = () => { setEditing(false); setTerm(''); setResults([]); setOpen(false); };
  const pick = (record) => { close(); onSelect(record); };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown' && results.length) { e.preventDefault(); setOpen(true); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp' && results.length) { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter' && open && results[active]) { e.preventDefault(); pick(results[active]); }
    else if (e.key === 'Escape') { e.preventDefault(); close(); }
  };

  if (!showInput) {
    if (!value) return <div style={{ ...BOX_STYLE, width, color: '#999', fontSize: 14, padding: '6px 10px', background: '#f6f7f9' }}>—</div>;
    return (
      <div style={{ ...BOX_STYLE, width, background: readOnly ? '#f6f7f9' : '#fff' }}>
        <RecordChip label={value.label} href={href} icon={Icon} origin={origin} noun={noun} hasUnsavedChanges={hasUnsavedChanges} title={openTitle} />
        {!readOnly && (
          <button type="button" onClick={() => setEditing(true)} title={changeTitle} aria-label={changeTitle} className="record-lookup-change">
            <Pencil size={13} strokeWidth={2} />
          </button>
        )}
        <style>{CHANGE_CSS}</style>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width, maxWidth: '100%' }}>
      <input
        ref={inputRef}
        className="styled-input"
        placeholder={value ? `Search to replace ${value.label}...` : placeholder}
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => { if (results.length) setOpen(true); }}
        // Delayed so a click on a result lands before the list closes.
        onBlur={() => setTimeout(() => { setOpen(false); if (value) close(); }, 150)}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        style={{ fontSize: 14, width: '100%', boxSizing: 'border-box' }}
      />
      {open && (
        <ul id={listId} role="listbox" style={{ position: 'absolute', zIndex: 10, top: '100%', left: 0, right: 0, margin: 0, padding: 4, listStyle: 'none', background: '#fff', border: '1px solid #bfc8d6', borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto' }}>
          {results.length === 0 ? (
            <li style={{ padding: '6px 8px', fontSize: 13, color: '#888' }}>{searching ? 'Searching...' : 'No matches'}</li>
          ) : results.map((r, i) => (
            <li
              key={r.id}
              role="option"
              aria-selected={i === active}
              onMouseDown={() => pick(r)}
              onMouseEnter={() => setActive(i)}
              style={{ padding: '6px 8px', cursor: 'pointer', fontSize: 13, borderRadius: 4, background: i === active ? '#e3eaf3' : 'transparent' }}
            >
              {optionLabel(r)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const CHANGE_CSS = `
.record-lookup-change {
  margin-left: auto; display: inline-flex; align-items: center; justify-content: center;
  width: 26px; height: 26px; padding: 0; border: none; background: none; border-radius: 4px;
  color: #5a6b85; cursor: pointer; flex-shrink: 0;
}
.record-lookup-change:hover { background: #eef1f5; color: #1976d2; }
`;

export default RecordLookupField;
